import { useQuery } from '@tanstack/react-query'
import { Link, Navigate, useNavigate } from 'react-router-dom'

import { api, unwrap } from '../api/client'
import type { ModuleNode } from '../api/types'
import { t, tOr } from '../i18n'
import { ErrorNotice, Notice, Pending } from '../ui'
import { StaleNotice } from '../ui/stale'
import { menuEntries } from './nav'
import { canOpen, placements, useSession, type ProjectPlacement } from './session'
import { useDocumentTitle } from './title'

/**
 * Where `/` goes: every job this person may open, and nothing else.
 *
 * **Why a screen rather than the redirect that was here.** `/` used to send
 * somebody to the first project they could open, and the only way to reach the
 * others was a `<select>` in the app bar they had to already know was there.
 * Somebody on six jobs landed on whichever sorted first. A person signing in
 * now sees the jobs they are on, whose they are and where, before they are
 * inside one of them.
 *
 * **This screen grants nothing and reveals nothing.** The list is exactly what
 * `GET /session/me` answered, which is the projects this person holds an active
 * membership on — the filter is the API's and is not re-done here. There is no
 * box to type a project identifier into, and there never may be: a project of
 * another customer is answered the way one that does not exist is answered, and
 * a screen that let somebody ask about an id would turn that refusal into an
 * existence oracle. A job missing from this list is a permission fact, which
 * this screen reports and does not repair.
 *
 * **Signing out lives here too, in both states.** This is the screen somebody
 * arrives on, and `App.tsx` mounts it outside the shell, so the shell's own
 * control is not on the page: without a button here a person on two jobs had
 * to enter one of them to end their session or change account. The owner found
 * that reading the component ([P2] on #258). It is the same `signOut` the empty
 * state already used and the same one the shell uses -- no second way to end a
 * session, because two of those is how one of them stops revoking anything.
 *
 * **What "the applications on this job" means, in two halves that are not the
 * same question.** The CM modules are drawn from this person's own levels on
 * that job, taken from the numbers the evaluator applies: CM holds that
 * permission and can answer it. The applications *beside* CM -- QA/QC,
 * lock-out/tag-out, Daily Report -- are drawn from the job's own switches, and
 * whether this person may open one of them is held over there, by that
 * application, and is not asked or copied here. A copy of another
 * application's permissions inside CM would be that application's authority
 * moved into this one, which is exactly what the owner said must not happen,
 * and wrong the first time it changed over there.
 *
 * So those entries cannot keep the CM rule below, and do not pretend to: they
 * are a labelled way out, and the other application refuses at its own door. A
 * refusal from the thing that owns the permission is honest; an entry missing
 * because CM guessed is not. `docs/post-sign-in-view.md` sets out what each of
 * them would have to expose for the guessing to stop.
 *
 * **A way out is not a sign-in.** Nothing travels with one: no session, no
 * token, no name and no project. The other application asks whoever arrives
 * who they are, and the caution under the list says so where somebody reads it
 * before they click rather than after.
 */
/** The applications beside CM, in the order somebody reads them off a card. */
const BESIDE = ['qaqc', 'loto', 'dailyreport'] as const

export function Projects() {
  // The tab says which screen this is; the heading below says it again for
  // the reader who can see it.
  useDocumentTitle(t('projects.title'))

  const { credentials, me, signOut: dropCredential } = useSession()
  const navigate = useNavigate()
  // Somebody who signs out here meant to leave, not to be shown the welcome
  // page the bare start address now gives a visitor, so they go to sign in.
  const signOut = () => {
    dropCredential()
    navigate('/signin', { replace: true })
  }
  const adminPath = me.data?.person.platform_role
    ? '/admin'
    : me.data?.owners.some((owner) => owner.tool_levels.directory === 'admin')
      ? '/admin/access'
      : null

  // One call for every job, made here rather than per card: a site with eight
  // jobs on the screen is eight requests otherwise, and they all answer from
  // the same table.
  const offered = useQuery({
    queryKey: ['session', 'project-applications'],
    queryFn: async () => unwrap(await api.GET('/directory/my-project-applications', {})),
    // A job with nothing switched on is absent from the answer, and a person
    // whose instance has never set one gets an empty list. Neither is an error
    // and neither stops the chooser drawing: the jobs are what matter here.
    retry: false,
  })
  // **An answer nobody got is not an answer of none.** `offered.data ?? []`
  // reads a failed read as every job having nothing beside CM, which is the
  // one wrong thing this screen must not say: a job with QA/QC on it would
  // look exactly like a job without, and a refresh that failed after a good
  // read would leave the old list on the screen with nothing saying so. So
  // the three cases are kept apart and the jobs are drawn in all of them --
  // the CM list is what this screen is for, and it never waits on this call.
  const besideUnavailable = Boolean(offered.error) && offered.data === undefined
  const besideStale = Boolean(offered.error) && offered.data !== undefined
  const retryOffered = () => void offered.refetch()

  if (me.isPending) return <Pending />
  // Only when there is nothing to show. `/session/me` is declared `retry: false`
  // and its answer is held five minutes, and nothing overrides
  // `refetchOnReconnect`, so it is the default true: a link that drops and comes
  // back, or a 502 from the proxy while the box restarts, fails one refresh and
  // sets `me.error` with `me.data` still good. `placements(me.data)` two lines
  // below is the proof the data is usable. This screen is the one somebody lands
  // on -- `Landing` is gone and this replaces it -- so deciding on the error
  // before looking at the data threw a person with a perfectly good session onto
  // "that did not work". A first read that fails still gets the retry, because
  // then there really is no list to draw. Same shape as #296 and #299; the fault
  // was found on this file and reported by the #297 thread.
  if (me.error && !me.data)
    return <ErrorNotice error={me.error} retry={() => void me.refetch()} />

  const all = placements(me.data)
  if (all.length === 0) {
    return (
      <div className="signin">
        <Notice tone="warn">{t('signin.no_projects')}</Notice>
        {/* Until this link was here, the only thing the application let
            somebody on no job do about it was sign out. The empty landing
            screen this used to live on is gone, and this is where somebody
            on no job now lands. */}
        <p>
          <Link to="/access">{t('signin.ask_for_access')}</Link>
        </p>
        {/* Somebody who administers may be on no job, and the way back from
            administration lands here; without this they could only type the
            address to return. The same link, for the same people, as above
            the list. */}
        {adminPath ? (
          <p>
            <Link to={adminPath}>{t('admin.rail.administration')}</Link>
          </p>
        ) : null}
        <button type="button" onClick={signOut}>
          {t('signin.sign_out')}
        </button>
      </div>
    )
  }

  // A tablet is not choosing anything. Its sign-in named a job — the keypad
  // sends it straight there — and this screen is only reached when somebody
  // already signed in opens `/device` again, or reloads onto `/`. Showing a
  // list of one to somebody standing in a trailer is a tap they have to make
  // to get back where they were, so a device credential goes to its job. A
  // device holding more than one job is not a thing the sign-in can produce;
  // if it ever were, the list is the right answer and is what it gets.
  if (credentials?.mode === 'device' && all.length === 1 && all[0]) {
    return <Navigate to={`/p/${all[0].project.id}`} replace />
  }

  // Grouped the way the switcher already groups them, so the two agree: the
  // customer, then the address, then the jobs at it. A person in two firms sees
  // two headings rather than one flat list with no clue whose job is whose.
  const groups = group(all)

  return (
    <div className="chooser">
      <header className="chooser-head">
        <h1>{t('projects.title')}</h1>
        <div className="chooser-actions">
          {/* The way to Administration, for the people the server lets in.
              Who runs the site lands on their own page. Somebody who
              administers the directory across a customer, by a company-scope
              grant, may open every screen on the rail, so they are taken to
              the first of them. A level held on one job opens none of the
              address-wide ones, so it is not enough for the link. */}
          {adminPath ? <Link to={adminPath}>{t('admin.rail.administration')}</Link> : null}
          <button type="button" onClick={signOut}>
            {t('signin.sign_out')}
          </button>
        </div>
      </header>
      <p className="muted">{t('projects.explain')}</p>
      {offered.isPending ? <p className="muted">{t('projects.beside.reading')}</p> : null}
      {besideUnavailable ? (
        <Notice tone="warn">
          {t('projects.beside.unavailable')}{' '}
          <button type="button" onClick={retryOffered}>
            {t('app.retry')}
          </button>
        </Notice>
      ) : null}
      {besideStale ? (
        <StaleNotice
          error={offered.error}
          retry={retryOffered}
          message="projects.beside.stale"
        />
      ) : null}
      {groups.map((grouped) => (
        <section key={`${grouped.ownerId}:${grouped.addressId}`}>
          <h2>
            {grouped.ownerName} · {grouped.addressLabel}
          </h2>
          <ul className="chooser-list">
            {grouped.entries.map((placement) => (
              <li key={placement.project.id}>
                <ProjectCard
                  placement={placement}
                  modules={me.data?.modules ?? []}
                  beside={
                    (offered.data ?? []).find(
                      (row) => String(row.project_id) === String(placement.project.id),
                    )?.applications ?? []
                  }
                  launchers={me.data?.launchers ?? {}}
                />
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  )
}

function ProjectCard({
  placement,
  modules,
  beside,
  launchers,
}: {
  placement: ProjectPlacement
  modules: ModuleNode[]
  /** Application codes this job has switched on. Not a statement about people. */
  beside: string[]
  /** Where each application is, for the ones this deployment has been told. */
  launchers: Record<string, string>
}) {
  // The same list the left-hand navigation builds for this job, kept to the
  // entries this person's levels actually open. A module they hold nothing on
  // is left out rather than shown greyed: this screen is the way in, and an
  // entry here would be a door that refuses.
  const open = menuEntries(modules, placement.project.id).filter((entry) =>
    canOpen(placement, entry.toolCode),
  )
  // A fixed order rather than the server's, so two jobs with the same
  // applications read the same way down a page. An application the server
  // names and this build does not know about is dropped rather than rendered
  // as its code: a card is not the place to learn a new vocabulary.
  const ordered = BESIDE.filter((app) => beside.includes(app))
  return (
    <div className="card">
      <h3>
        <Link to={`/p/${placement.project.id}`}>
          <span className="mono">{placement.project.number}</span> {placement.project.name}
        </Link>
      </h3>
      {open.length === 0 ? (
        <p className="muted">{t('projects.nothing_open')}</p>
      ) : (
        <p className="muted">
          {t('projects.you_can_open')} {open.map((entry) => entry.label).join(' · ')}
        </p>
      )}
      {ordered.length > 0 ? (
        <div className="beside">
          <p className="muted">{t('projects.beside.title')}</p>
          <ul>
            {ordered.map((app) => {
              const address = launchers[app]
              const label = tOr(`projects.beside.${app}`, app)
              return (
                <li key={app}>
                  {address ? (
                    // `noreferrer` as well as `noopener`: the second stops the
                    // page reaching back into this one, the first stops this
                    // job's own address travelling as a referrer, which is the
                    // quietest way an identifier nobody meant to send arrives.
                    <a href={address} target="_blank" rel="noopener noreferrer">
                      {label}
                    </a>
                  ) : (
                    <>
                      {label}{' '}
                      <span className="muted">{t('projects.beside.no_address')}</span>
                    </>
                  )}
                </li>
              )
            })}
          </ul>
          <p className="hint">{t('projects.beside.caution')}</p>
        </div>
      ) : null}
    </div>
  )
}

type Group = {
  ownerId: string
  ownerName: string
  addressId: string
  addressLabel: string
  entries: ProjectPlacement[]
}

/** Owner, then address, in the order `/session/me` already sorted them. */
function group(all: ProjectPlacement[]): Group[] {
  const groups: Group[] = []
  for (const placement of all) {
    const last = groups[groups.length - 1]
    if (
      last &&
      last.ownerId === placement.owner.id &&
      last.addressId === placement.address.id
    ) {
      last.entries.push(placement)
      continue
    }
    groups.push({
      ownerId: placement.owner.id,
      ownerName: placement.owner.name,
      addressId: placement.address.id,
      addressLabel: placement.address.label,
      entries: [placement],
    })
  }
  return groups
}
